/**
 * Dezimaleingaben der Kontostand-Erfassung (deutsche Schreibweise) in die Form bringen, die der
 * Server erwartet: Punkt als Dezimaltrenner, keine Tausendertrenner, keine Leerzeichen.
 *
 * Regeln:
 * - Fuehrende/abschliessende Leerzeichen (auch geschuetzte/schmale) werden getrimmt. Leerzeichen
 *   INNERHALB einer Zahl ("1 1632") werden nie stillschweigend entfernt (sonst 11632), sondern
 *   mit Hinweis abgelehnt.
 * - Mehr Nachkommastellen als `maxNachkommastellen` werden mit Nennung der erlaubten Stellenzahl
 *   abgelehnt (Betrag USD: 2, Mischkurs: 10 - wie numeric(18,2)/numeric(18,10) und die Backend-Pruefung).
 * - Kommt Komma UND Punkt vor, ist das LETZTE der beiden der Dezimaltrenner, das andere trennt
 *   Tausender ("1.234,56" und "1,234.56" werden beide zu "1234.56").
 * - Nur Komma: genau eins ist der Dezimaltrenner ("100,50"), mehrere sind Tausendertrenner.
 * - Nur Punkt: mehrere sind Tausendertrenner ("1.234.567"), ein einzelner ist Dezimaltrenner.
 *
 * Strenge bei Betraegen (hoechstens 2 Nachkommastellen, `maxNachkommastellen < 3`): Geld wird nie
 * geraten.
 * - Ein einzelner Trenner vor genau drei Ziffern ist mehrdeutig ("1.234": 1234 oder 1,234?) und
 *   wird abgelehnt. Steht davor eine fuehrende "0" ("0.123"), ist er ein Dezimaltrenner und die
 *   Eingabe hat zu viele Nachkommastellen - ebenfalls abgelehnt.
 * - Tausendergruppen werden strikt geprueft: erste Gruppe 1-3 Ziffern, jede weitere genau 3
 *   ("1.2,5" und "12.34.567" sind Fehler).
 * Bei Kursen (mehr Nachkommastellen) bleibt ein einzelner Trenner immer Dezimaltrenner. Kurse haben
 * keine Tausendergruppen: mehr als ein Trenner ("1.16.32", "1.163,2") wird abgelehnt statt verbunden.
 *
 * Text, der keine Zahl ist (Buchstaben, Minus, leer), kommt unveraendert (getrimmt) als Wert
 * zurueck und faellt bei der Gueltigkeitspruefung des Servers durch.
 */
export type Dezimalergebnis = { wert: string } | { fehler: string };

function tausenderStrikt(ganzzahl: string, trenner: string): string | null {
  const gruppen = ganzzahl.split(trenner);
  if (gruppen.length === 1) return ganzzahl;
  if (!/^\d{1,3}$/.test(gruppen[0]) || !gruppen.slice(1).every((g) => /^\d{3}$/.test(g))) return null;
  return gruppen.join("");
}

export function liesDezimaleingabe(eingabe: string, maxNachkommastellen = 6): Dezimalergebnis {
  const text = eingabe.replace(/^[\s  ]+|[\s  ]+$/g, "");
  const streng = maxNachkommastellen < 3;
  if (/[\s  ]/.test(text)) {
    return { fehler: `Bitte ohne Leerzeichen eingeben, z. B. ${streng ? "1234,50" : "1,1632"}` };
  }
  if (!/^[\d.,]+$/.test(text)) return { wert: text };
  const zuVieleStellen = { fehler: `Zu viele Nachkommastellen (höchstens ${maxNachkommastellen} erlaubt)` };
  const mitStellenpruefung = (wert: string): Dezimalergebnis =>
    (wert.split(".")[1] ?? "").length > maxNachkommastellen ? zuVieleStellen : { wert };
  const letzterPunkt = text.lastIndexOf(".");
  const letztesKomma = text.lastIndexOf(",");
  const anzahl = (zeichen: string) => text.split(zeichen).length - 1;
  const gruppenFehler = { fehler: "Tausendergruppen ungültig" };
  if (letzterPunkt >= 0 && letztesKomma >= 0) {
    const dezimal = letzterPunkt > letztesKomma ? "." : ",";
    const tausend = dezimal === "." ? "," : ".";
    if (!streng) return { fehler: "Nur ein Dezimaltrenner erlaubt" };
    if (anzahl(dezimal) !== 1) return gruppenFehler;
    const [ganz, nach] = text.split(dezimal);
    const ganzOhne = tausenderStrikt(ganz, tausend);
    if (ganzOhne === null) return gruppenFehler;
    return mitStellenpruefung(`${ganzOhne}.${nach}`);
  }
  const trenner = letzterPunkt >= 0 ? "." : letztesKomma >= 0 ? "," : "";
  if (!trenner) return { wert: text };
  if (anzahl(trenner) > 1) {
    if (!streng) return { fehler: "Nur ein Dezimaltrenner erlaubt" };
    const ohne = tausenderStrikt(text, trenner);
    return ohne === null ? gruppenFehler : { wert: ohne };
  }
  const position = text.indexOf(trenner);
  const nachkomma = text.length - position - 1;
  if (streng && nachkomma === 3) {
    return text.slice(0, position) === "0" ? zuVieleStellen : { fehler: "Eingabe mehrdeutig" };
  }
  return mitStellenpruefung(text.replace(trenner, "."));
}

/** USD-Text mit hoechstens zwei Nachkommastellen in ganze Cent; `null`, wenn es keiner ist. */
export function usdInCent(text: string): bigint | null {
  const treffer = /^(\d{1,16})(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!treffer) return null;
  return BigInt(treffer[1]) * 100n + BigInt((treffer[2] ?? "").padEnd(2, "0"));
}

/** Ganze Cent als "1234.50" (Punkt, immer zwei Nachkommastellen). */
export function centAlsUsdText(cent: bigint): string {
  const euro = cent / 100n;
  const rest = (cent % 100n).toString().padStart(2, "0");
  return `${euro}.${rest}`;
}
